import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from "react";
import { api } from "@/lib/api";
import heroBerzosaImg from "@/assets/hero-berzosa.jpg";
import heroCabreraImg from "@/assets/hero-cabrera.jpg";
import type { NavButton } from "@/components/MunicipalityForm";

export type { NavButton };

export interface TotemConfig {
  city: string;
  lat: number;
  lon: number;
  municipality: string;
  municipalityId: string;
  apiKey?: string;
  heroes: Array<{
    name: string;
    imageUrl: string;
    clickUrl: string;
  }>;
  navButtons: NavButton[];
  bottomButtons: NavButton[];
  newsletterUrl: string;
  carpetaniaUrl: string;
  enjoyUrl: string;
  primaryColor?: string;
  backgroundColor?: string;
  inactivityPromptEnabled: boolean;
  isLoading: boolean;
}

interface MunicipalityAssets {
  id?: string;
  heroes: Array<{
    name: string;
    imageUrl: string;
    clickUrl: string;
  }>;
  navButtons: NavButton[];
  bottomButtons: NavButton[];
  newsletterUrl: string;
  carpetaniaUrl: string;
  enjoyUrl: string;
  primaryColor?: string;
  backgroundColor?: string;
  inactivityPromptEnabled: boolean;
}

interface HeroDataDB {
  name: string;
  image_url: string;
  click_url: string;
}

// Fallback configuration map (used when DB is not available)
const FALLBACK_MUNICIPALITY_CONFIGS: Record<string, MunicipalityAssets> = {
  "Lugo": {
    heroes: [{
      name: "Lugo",
      imageUrl: heroBerzosaImg,
      clickUrl: "https://totems.sierranortemadrid.org/?pueblo=Berzosa%20del%20Lozoya"
    }],
    navButtons: [],
    bottomButtons: [],
    newsletterUrl: "https://totems.sierranortemadrid.org/newsletter",
    carpetaniaUrl: "https://totems.sierranortemadrid.org/carpetania",
    enjoyUrl: "https://totems.sierranortemadrid.org/enjoy",
    inactivityPromptEnabled: false,
  },
  "La Cabrera": {
    heroes: [{
      name: "La Cabrera",
      imageUrl: heroCabreraImg,
      clickUrl: "https://totems.sierranortemadrid.org/?pueblo=La%20Cabrera"
    }],
    navButtons: [],
    bottomButtons: [],
    newsletterUrl: "https://totems.sierranortemadrid.org/newsletter",
    carpetaniaUrl: "https://totems.sierranortemadrid.org/carpetania",
    enjoyUrl: "https://totems.sierranortemadrid.org/enjoy",
    inactivityPromptEnabled: false,
  }
};

const URLConfigContext = createContext<TotemConfig | null>(null);

export const URLConfigProvider = ({ children }: { children: ReactNode }) => {
  const [municipalitiesFromDB, setMunicipalitiesFromDB] = useState<Record<string, MunicipalityAssets>>({});
  const [dbLoaded, setDbLoaded] = useState(false);

  // Almacenar los params de URL iniciales una sola vez al montar
  // Esto previene que navigate('/') cause pérdida de configuración
  const [initialParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      city: params.get('city') || params.get('municipality') || "Lugo",
      lat: parseFloat(params.get('lat') || '40.9083'),
      lon: parseFloat(params.get('lon') || '-3.5167'),
      apiKey: params.get('apiKey') || ''
    };
  });

  useEffect(() => {
    const loadMunicipalities = async () => {
      try {
        const data = await api.municipalities.list();

        if (data && data.length > 0) {
          const municipalitiesMap: Record<string, MunicipalityAssets> = {};
          data.forEach((municipality: any) => {
            const heroesData = municipality.heroes_data as HeroDataDB[];
            const heroes = Array.isArray(heroesData) && heroesData.length > 0
              ? heroesData.filter(hero => Boolean(hero.image_url)).map(hero => ({
                name: hero.name,
                imageUrl: hero.image_url,
                clickUrl: hero.click_url
              }))
              : [];

            municipalitiesMap[municipality.name] = {
              id: municipality.id,
              heroes,
              navButtons: (municipality.nav_buttons as NavButton[]) ?? [],
              bottomButtons: (municipality.bottom_buttons as NavButton[]) ?? [],
              newsletterUrl: municipality.newsletter_url,
              carpetaniaUrl: municipality.carpetania_url,
              enjoyUrl: municipality.enjoy_url,
              primaryColor: municipality.primary_color,
              backgroundColor: municipality.background_color,
              inactivityPromptEnabled: municipality.inactivity_prompt_enabled ?? false,
            };
          });
          setMunicipalitiesFromDB(municipalitiesMap);
        }
      } catch (error) {
        console.error('Error in loadMunicipalities:', error);
      } finally {
        setDbLoaded(true);
      }
    };

    loadMunicipalities();

    // Reload cuando la pestaña recupera el foco (sin realtime de Supabase)
    const handleFocus = () => loadMunicipalities();
    window.addEventListener('focus', handleFocus);
    window.addEventListener('municipalityConfigUpdated', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('municipalityConfigUpdated', handleFocus);
    };
  }, []);

  const config = useMemo<TotemConfig>(() => {
    // Usar los valores almacenados en estado en lugar de window.location.search
    // para evitar pérdida de configuración cuando navigate('/') limpia los params
    const { city, lat, lon, apiKey } = initialParams;

    // Use DB data if loaded, otherwise fallback to hardcoded config
    const MUNICIPALITY_CONFIGS = dbLoaded && Object.keys(municipalitiesFromDB).length > 0
      ? municipalitiesFromDB
      : FALLBACK_MUNICIPALITY_CONFIGS;

    // Resolver con varios fallbacks para nunca devolver undefined:
    // 1) municipio solicitado, 2) Lugo, 3) primer disponible
    const fallbackKey = Object.keys(MUNICIPALITY_CONFIGS)[0];
    const municipalityAssets =
      MUNICIPALITY_CONFIGS[city] ??
      MUNICIPALITY_CONFIGS['Lugo'] ??
      (fallbackKey ? MUNICIPALITY_CONFIGS[fallbackKey] : undefined);

    if (!municipalityAssets) {
      // BD vacía y sin fallback: devolver una configuración mínima en lugar de crashear
      return {
        city,
        lat,
        lon,
        municipality: city,
        municipalityId: '',
        apiKey,
        heroes: [],
        navButtons: [],
        bottomButtons: [],
        newsletterUrl: '',
        carpetaniaUrl: '',
        enjoyUrl: '',
        primaryColor: '#005041',
        backgroundColor: '#9EBEB3',
        inactivityPromptEnabled: false,
        isLoading: !dbLoaded,
      };
    }

    return {
      city,
      lat,
      lon,
      municipality: city,
      municipalityId: municipalityAssets.id || '',
      apiKey,
      heroes: municipalityAssets.heroes,
      navButtons: municipalityAssets.navButtons,
      bottomButtons: municipalityAssets.bottomButtons,
      newsletterUrl: municipalityAssets.newsletterUrl,
      carpetaniaUrl: municipalityAssets.carpetaniaUrl,
      enjoyUrl: municipalityAssets.enjoyUrl,
      primaryColor: municipalityAssets.primaryColor ?? '#005041',
      backgroundColor: municipalityAssets.backgroundColor ?? '#9EBEB3',
      inactivityPromptEnabled: municipalityAssets.inactivityPromptEnabled,
      isLoading: !dbLoaded
    };
  }, [municipalitiesFromDB, dbLoaded, initialParams]);

  return (
    <URLConfigContext.Provider value={config}>
      {children}
    </URLConfigContext.Provider>
  );
};

export const useURLConfigContext = (): TotemConfig => {
  const context = useContext(URLConfigContext);
  if (!context) {
    throw new Error('useURLConfigContext must be used within a URLConfigProvider');
  }
  return context;
};
